/**
 * Quill AI Styles — the four style catalogues Quill owns, in the LifeScribe AI
 * Styles admin's visual language, backed by Quill's own tables.
 *
 * Layout mirrors the original: teal header with dot texture and stat chips, four
 * catalogue tabs plus Analytics, a two-column card grid with drag handles and
 * position circles, and the 290px App Grid Control panel with 2×2 drop slots and
 * a mini phone preview.
 *
 * The model-defaults strip, the Analytics layout and the Export button are all
 * the original's. Where Quill's control plane has no equivalent for one of the
 * original's controls, the control still renders in its place — disabled, with a
 * tooltip naming where that setting actually lives. Nothing renders as
 * enabled-but-inert. See ModelDefaultsBar for the per-control ledger.
 *
 * SIX DELIBERATE DEPARTURES from the LifeScribe original (decisions, not gaps):
 *
 *  1. No per-row model override. Quill resolves models per pipeline, not per
 *     style, so a per-card dropdown would be a rival source of truth. The strip
 *     above sets Quill's one global writing default; the footer links out to
 *     Pipeline Models for everything else.
 *  2. Analytics keeps the original's four-tile-plus-two-pane layout but never
 *     fabricates. The original's "Usage Over Time" series is Math.random() and
 *     its usage/rating counters are always zero. Here the usage figures come
 *     from Quill's own recorded composes (migration 570), every one of them
 *     carries the date it is counted since, a style with no rows is omitted
 *     rather than shown as zero, and "not recorded yet", "not recording here"
 *     and "the request failed" are three different states with three different
 *     messages. Avg Rating stays an em dash — nothing collects ratings.
 *  3. Four LifeScribe bugs are fixed rather than copied: author delete hit the
 *     wrong table; "Add" from the Authors tab always created a genre (here Add
 *     writes to the OPEN catalogue); the illustration prompt fields were
 *     collected and dropped (here they persist through PUT); Export called a
 *     route that 404s and always exported "writing" (here it is a client-side
 *     download of the catalogue you are actually looking at).
 *  4. NEW `ls_style_key` field in the editor, with helper text explaining that
 *     LifeScribe's transformer resolves by key — the plain-language form of the
 *     backend's 400 HONESTY_GATE.
 *  5. Both illustration tabs carry a banner saying exactly how the catalogue
 *     reaches the app — by the row's LifeScribe style key, on new chapter
 *     pictures only, with no default for a member who never chose.
 *  6. Withheld rows render their `unavailable_reason` verbatim, and
 *     "Import from LifeScribe" is offered on the Writing Authors tab behind a
 *     production confirmation.
 *
 * Every production write except the debounced reorder routes through
 * QuillActionConfirm with the standing caption.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Link as MuiLink, Snackbar, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { Link as RouterLink } from 'react-router-dom';

import { useQuillHealth } from '../QuillHealthContext';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors } from '../../../theme/designTokens';
import { QuillApiError } from '../../../services/quillAdminService';

import AIStylesHeader from './AIStylesHeader';
import AIStylesTabBar from './AIStylesTabBar';
import ModelDefaultsBar from './ModelDefaultsBar';
import StyleCardGrid from './StyleCardGrid';
import StyleDragOverlay from './StyleDragOverlay';
import AppGridControlPanel from './AppGridControlPanel';
import UnsavedOrderBar from './UnsavedOrderBar';
import StyleEditDialog from './StyleEditDialog';
import AnalyticsPanel, { CatalogueSummary } from './AnalyticsPanel';
import { aiStylesApi, rowKey } from './api';
import { useStylesData } from './useStylesData';
import { useDragReorder } from './useDragReorder';
import {
  ANALYTICS_TAB_INDEX,
  ILLUSTRATION_BANNER,
  KEY_FIELD,
  MAX_ACTIVE_PER_CATALOGUE,
  PRODUCTION_CAPTION,
  TAB_CONFIGS,
  USAGE_CATEGORY_BY_CATALOGUE,
} from './constants';
import { CatalogueKey, StyleFamily, StyleRow, StyleWritePayload, TabConfig } from './types';

type Severity = 'success' | 'error' | 'info' | 'warning';

/**
 * Every column an admin needs to review a style offline, in a stable order.
 * Absent columns are exported as `null` rather than dropped, so two exports of
 * the same catalogue are always diffable.
 */
const EXPORT_FIELDS = [
  'display_name',
  'description',
  'display_description',
  'sample_excerpt',
  'style_dna',
  'transformation_formula',
  'truth_integration',
  'visual_approach',
  'prompt_construction',
  'ls_style_key',
  'is_active',
  'is_premium',
  'unavailable_reason',
  'sort_order',
  'source',
  'style_type',
  'created_at',
  'updated_at',
] as const;

/** Plain-language rendering of the backend's two named refusals. */
function describeError(e: unknown, fallback: string): string {
  if (e instanceof QuillApiError) {
    if (e.code === 'MAX_ACTIVE_REACHED' || e.status === 409) {
      return `A catalogue can have at most ${MAX_ACTIVE_PER_CATALOGUE} active styles — the app shows them in a 2×2 grid. Deactivate one first.`;
    }
    if (e.code === 'HONESTY_GATE') {
      return `This style has no LifeScribe style key, so LifeScribe's transformer can't resolve it. Offering it would promise something the app can't deliver — set a LifeScribe style key first. (${e.message})`;
    }
    return e.message;
  }
  return e instanceof Error ? e.message : fallback;
}

interface PendingToggle {
  kind: 'toggle';
  tab: TabConfig;
  row: StyleRow;
  key: string;
  nextActive: boolean;
}
interface PendingDelete {
  kind: 'delete';
  tab: TabConfig;
  row: StyleRow;
  key: string;
}
interface PendingPopular {
  kind: 'popular';
  family: StyleFamily;
  value: string;
}
interface PendingImport {
  kind: 'import';
}
type PendingAction = PendingToggle | PendingDelete | PendingPopular | PendingImport;

const AIStylesPage: React.FC = () => {
  const { status } = useQuillHealth();
  const { catalogues, popular, setPopularLocal, loading, refreshing, errors, reload, reloadCatalogue } =
    useStylesData();

  const [tabValue, setTabValue] = useState(0);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<StyleRow | null>(null);
  const [editingKey, setEditingKey] = useState('');
  const [editorTab, setEditorTab] = useState<TabConfig>(TAB_CONFIGS[0]);
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: Severity }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const notify = useCallback(
    (message: string, severity: Severity = 'success') =>
      setSnackbar({ open: true, message, severity }),
    []
  );

  const {
    sensors,
    collisionDetection,
    activeDragName,
    unsavedCatalogues,
    savingCatalogue,
    getOrdered,
    moveByKey,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
    saveOrder,
    discardOrder,
  } = useDragReorder();

  const currentTab: TabConfig | null = tabValue < TAB_CONFIGS.length ? TAB_CONFIGS[tabValue] : null;

  // ── Derived counts (all from rows the API actually returned) ──────────────

  const summaries: CatalogueSummary[] = useMemo(
    () =>
      TAB_CONFIGS.map((tab) => {
        const rows = catalogues[tab.catalogue]?.rows ?? [];
        return {
          catalogue: tab.catalogue,
          label: tab.label,
          family: tab.family,
          total: rows.length,
          active: rows.filter((r) => r.is_active).length,
          withheld: rows.filter((r) => r.unavailable_reason).length,
          missingLsKey: rows.filter((r) => !r.ls_style_key).length,
        };
      }),
    [catalogues]
  );

  /**
   * Display names for the Analytics usage list, keyed
   * `<usage category>:<style key>`. A LOOKUP, not a join: the usage endpoint is
   * the only authority on which styles were used, and a key it did not return
   * must not acquire a row here (nor a zero). A key that is not in this map is
   * rendered as the raw key — a usage row outlives the catalogue row it names.
   */
  const styleNames = useMemo(() => {
    const map: Record<string, string> = {};
    TAB_CONFIGS.forEach((tab) => {
      const category = USAGE_CATEGORY_BY_CATALOGUE[tab.catalogue];
      (catalogues[tab.catalogue]?.rows ?? []).forEach((row) => {
        const key = rowKey(row, tab.catalogue);
        if (key && row.display_name) map[`${category}:${key}`] = row.display_name;
      });
    });
    return map;
  }, [catalogues]);

  const withheldRows = useMemo(
    () =>
      TAB_CONFIGS.flatMap((tab) =>
        (catalogues[tab.catalogue]?.rows ?? [])
          .filter((row) => row.unavailable_reason)
          .map((row) => ({ key: rowKey(row, tab.catalogue), label: tab.label, row }))
      ),
    [catalogues]
  );

  const headerStats = useMemo(() => {
    const totalCount = summaries.reduce((sum, s) => sum + s.total, 0);
    const activeCount = summaries.reduce((sum, s) => sum + s.active, 0);
    const withheldCount = summaries.reduce((sum, s) => sum + s.withheld, 0);
    const inGridCount = summaries.reduce(
      (sum, s) => sum + Math.min(s.active, MAX_ACTIVE_PER_CATALOGUE),
      0
    );
    return {
      activeCount,
      catalogueCount: TAB_CONFIGS.length,
      inGridCount,
      totalCount,
      withheldCount,
    };
  }, [summaries]);

  const tabCounts = summaries.map((s) => s.total);

  // ── Actions ──────────────────────────────────────────────────────────────

  const toggleExpand = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleCopy = (text: string, label: string) => {
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clipboard) {
      notify('Clipboard is not available in this browser.', 'error');
      return;
    }
    clipboard
      .writeText(text)
      .then(() => notify(`${label} copied to clipboard`, 'info'))
      .catch(() => notify('Failed to copy to clipboard', 'error'));
  };

  /**
   * Export the OPEN catalogue as JSON, entirely client-side — the rows are
   * already in memory, so there is no endpoint to call and nothing to 404.
   * Download mechanics are the original's: Blob → createObjectURL → an anchor
   * with a `download` attribute → revoke.
   */
  const handleExport = () => {
    if (!currentTab) return;
    const result = catalogues[currentTab.catalogue];
    const rows = result?.rows ?? [];
    const stamp = new Date().toISOString().slice(0, 10);

    const payload = {
      catalogue: currentTab.catalogue,
      label: currentTab.label,
      key_field: KEY_FIELD[currentTab.catalogue],
      exported_at: new Date().toISOString(),
      row_count: rows.length,
      active_count: result?.active_count ?? rows.filter((r) => r.is_active).length,
      rows: rows.map((row) => {
        const out: Record<string, unknown> = {
          key: rowKey(row, currentTab.catalogue),
        };
        EXPORT_FIELDS.forEach((field) => {
          out[field] = row[field] ?? null;
        });
        return out;
      }),
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `quill-${currentTab.catalogue}-${stamp}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    notify(`${currentTab.label} exported — ${rows.length} row${rows.length === 1 ? '' : 's'}.`);
  };

  const openCreate = () => {
    if (!currentTab) return;
    setEditorTab(currentTab);
    setEditingRow(null);
    setEditingKey('');
    setEditorOpen(true);
  };

  const openEdit = (tab: TabConfig) => (row: StyleRow) => {
    setEditorTab(tab);
    setEditingRow(row);
    setEditingKey(rowKey(row, tab.catalogue));
    setEditorOpen(true);
  };

  const submitEditor = async (payload: StyleWritePayload) => {
    const tab = editorTab;
    setSaving(true);
    try {
      if (editingRow) {
        await aiStylesApi.update(tab.catalogue, editingKey, payload);
        notify(`"${payload.display_name}" saved.`);
      } else {
        await aiStylesApi.create(tab.catalogue, payload);
        notify(`"${payload.display_name}" created in ${tab.label}.`);
      }
      setEditorOpen(false);
      await reloadCatalogue(tab.catalogue);
    } catch (e) {
      notify(describeError(e, 'Failed to save style.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runPending = async () => {
    if (!pending) return;
    setActionBusy(true);
    try {
      if (pending.kind === 'toggle') {
        setBusyKey(pending.key);
        await aiStylesApi.toggle(pending.tab.catalogue, pending.key, pending.nextActive);
        notify(
          `"${pending.row.display_name}" is now ${pending.nextActive ? 'active' : 'inactive'}.`
        );
        await reloadCatalogue(pending.tab.catalogue);
      } else if (pending.kind === 'delete') {
        setBusyKey(pending.key);
        await aiStylesApi.remove(pending.tab.catalogue, pending.key);
        notify(`"${pending.row.display_name}" removed from ${pending.tab.label}.`);
        await reloadCatalogue(pending.tab.catalogue);
      } else if (pending.kind === 'popular') {
        const patch =
          pending.family === 'writing'
            ? { writing: pending.value }
            : { illustration: pending.value };
        await aiStylesApi.setPopularCategory(patch);
        setPopularLocal({ ...popular, ...patch });
        notify(`"Most Popular" now sits with the ${pending.value} catalogue.`);
      } else {
        const result = await aiStylesApi.importFromLifeScribe();
        const parts: string[] = [];
        if (typeof result.imported === 'number') parts.push(`${result.imported} imported`);
        if (typeof result.updated === 'number') parts.push(`${result.updated} updated`);
        if (typeof result.skipped === 'number') parts.push(`${result.skipped} skipped`);
        notify(
          parts.length > 0
            ? `Import complete — ${parts.join(', ')}.`
            : result.message || 'Import complete.'
        );
        await reload({ silent: true });
      }
      setPending(null);
    } catch (e) {
      notify(describeError(e, 'The action could not be completed.'), 'error');
      setPending(null);
    } finally {
      setActionBusy(false);
      setBusyKey(null);
    }
  };

  const handleSaveOrder = async (tab: TabConfig) => {
    const rows = catalogues[tab.catalogue]?.rows ?? [];
    const result = await saveOrder(tab.catalogue, rows);
    if (result.ok) notify(`Order saved for ${tab.label}.`);
    else notify(result.message || 'Failed to save order.', 'error');
  };

  // ── Confirmation copy ────────────────────────────────────────────────────

  const confirmCopy = (() => {
    if (!pending) return null;
    if (pending.kind === 'toggle') {
      return {
        title: pending.nextActive ? 'Activate style' : 'Deactivate style',
        message: (
          <>
            {pending.nextActive ? 'Offer ' : 'Stop offering '}
            <strong>{pending.row.display_name}</strong> to members in the{' '}
            <strong>{pending.tab.label}</strong> catalogue.
          </>
        ),
        detail:
          pending.nextActive && !pending.row.ls_style_key ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              This style has no LifeScribe style key — the backend will refuse this activation.
            </Typography>
          ) : undefined,
        confirmLabel: pending.nextActive ? 'Activate' : 'Deactivate',
        destructive: false,
      };
    }
    if (pending.kind === 'delete') {
      return {
        title: `Remove ${pending.tab.noun}`,
        message: (
          <>
            Remove <strong>{pending.row.display_name}</strong> from the{' '}
            <strong>{pending.tab.label}</strong> catalogue. This is a soft delete — the row stays in
            the database and stops being offered.
          </>
        ),
        detail: undefined,
        confirmLabel: 'Remove style',
        destructive: true,
      };
    }
    if (pending.kind === 'popular') {
      return {
        title: 'Move the "Most Popular" badge',
        message: (
          <>
            Give the &ldquo;Most Popular&rdquo; ribbon to the <strong>{pending.value}</strong>{' '}
            catalogue. Members see it on the first style in that catalogue&rsquo;s grid.
          </>
        ),
        detail: undefined,
        confirmLabel: 'Move badge',
        destructive: false,
      };
    }
    return {
      title: 'Import authors from LifeScribe',
      message: (
        <>
          Copy LifeScribe&rsquo;s author catalogue into Quill&rsquo;s{' '}
          <strong>Writing Authors</strong> table. Existing rows with the same key are updated; the
          import does not activate anything on its own.
        </>
      ),
      detail: undefined,
      confirmLabel: 'Run import',
      destructive: false,
    };
  })();

  // ── Render ───────────────────────────────────────────────────────────────

  const loadErrorList = Object.entries(errors) as Array<[CatalogueKey, string]>;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#EDE5D6' }}>
      <AIStylesHeader
        stats={headerStats}
        addLabel={currentTab ? `Add ${currentTab.noun}` : 'Add style'}
        onAdd={openCreate}
        onRefresh={() => reload({ silent: true })}
        refreshing={refreshing}
        onImport={
          currentTab?.catalogue === 'writing-authors'
            ? () => setPending({ kind: 'import' })
            : undefined
        }
        onExport={handleExport}
        exportLabel={currentTab?.label ?? 'style'}
        healthStatus={status}
      />

      {/* Global model defaults — the original's strip, on Quill's control plane. */}
      <ModelDefaultsBar
        slots={['text', 'image', 'video']}
        notify={notify}
        footer={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.72rem',
                color: '#64748b',
              }}
            >
              The writing slot above is the <code>conversation_main</code> pipeline; every other
              pipeline is set on{' '}
              <MuiLink
                component={RouterLink}
                to="/dashboard/quill/pipeline-models"
                sx={{ color: colors.deepTeal[500], fontWeight: 600 }}
              >
                Pipeline Models
              </MuiLink>
              . No model here writes the chapter prose these styles shape — LifeScribe&rsquo;s
              transformer does that, resolving each style by its LifeScribe style key.
            </Typography>
          </Box>
        }
      />

      <AIStylesTabBar
        tabValue={tabValue}
        onChange={setTabValue}
        counts={tabCounts}
        unsavedCatalogues={unsavedCatalogues}
      />

      {loadErrorList.length > 0 && (
        <Box sx={{ px: 3, pt: 2 }}>
          {loadErrorList.map(([catalogue, message]) => (
            <Alert key={catalogue} severity="error" sx={{ mb: 1 }}>
              {TAB_CONFIGS.find((t) => t.catalogue === catalogue)?.label || catalogue}: {message}
            </Alert>
          ))}
        </Box>
      )}

      {TAB_CONFIGS.map((tab, index) => {
        if (tabValue !== index) return null;
        const rows = catalogues[tab.catalogue]?.rows ?? [];
        const ordered = getOrdered(tab.catalogue, rows);
        const activeOrdered = ordered.filter((i) => i.isActive);
        const rowsByKey = new Map(rows.map((row) => [rowKey(row, tab.catalogue), row]));
        const isMostPopularCatalogue = popular[tab.popularField] === tab.popularValue;

        return (
          <Box
            key={tab.catalogue}
            role="tabpanel"
            id={`quill-styles-tabpanel-${index}`}
            aria-label={tab.label}
          >
            {/*
              Departure 5 — a screen may only claim what the code does. This
              banner said "not yet wired to the app" until build 23 wired it;
              the copy now describes the live path (constants.ts).
            */}
            {tab.family === 'illustration' && (
              <Alert severity="info" sx={{ borderRadius: 0 }}>
                {ILLUSTRATION_BANNER}
              </Alert>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={collisionDetection}
              onDragStart={(e) => handleDragStart(e, ordered)}
              onDragOver={handleDragOver}
              onDragCancel={handleDragCancel}
              onDragEnd={(e) => handleDragEnd(e, tab.catalogue, rows)}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 290px' },
                  minHeight: 'calc(100vh - 320px)',
                }}
              >
                <StyleCardGrid
                  orderedItems={ordered}
                  rowsByKey={rowsByKey}
                  family={tab.family}
                  pillLabel={tab.pillLabel}
                  isMostPopularCatalogue={isMostPopularCatalogue}
                  loading={loading}
                  expandedKeys={expandedKeys}
                  busyKey={busyKey}
                  onToggle={(row, nextActive) =>
                    setPending({
                      kind: 'toggle',
                      tab,
                      row,
                      key: rowKey(row, tab.catalogue),
                      nextActive,
                    })
                  }
                  onEdit={openEdit(tab)}
                  onDelete={(row) =>
                    setPending({ kind: 'delete', tab, row, key: rowKey(row, tab.catalogue) })
                  }
                  onExpandToggle={toggleExpand}
                  onMove={(key, direction) => moveByKey(tab.catalogue, rows, key, direction)}
                  onCopy={handleCopy}
                />

                <AppGridControlPanel
                  activeItems={activeOrdered}
                  stats={{
                    inGrid: Math.min(activeOrdered.length, MAX_ACTIVE_PER_CATALOGUE),
                    total: ordered.length,
                    inactive: ordered.filter((i) => !i.isActive).length,
                    withheld: rows.filter((r) => r.unavailable_reason).length,
                  }}
                  isMostPopularCatalogue={isMostPopularCatalogue}
                  family={tab.family}
                  popularValue={popular[tab.popularField]}
                  onTogglePopular={(family, value) => setPending({ kind: 'popular', family, value })}
                />
              </Box>

              <DragOverlay>
                {activeDragName ? <StyleDragOverlay name={activeDragName} /> : null}
              </DragOverlay>
            </DndContext>

            {/* Failure path only — reordering normally saves silently. */}
            <UnsavedOrderBar
              visible={unsavedCatalogues.has(tab.catalogue)}
              saving={savingCatalogue === tab.catalogue}
              onSave={() => handleSaveOrder(tab)}
              onDiscard={() => discardOrder(tab.catalogue)}
            />
          </Box>
        );
      })}

      {tabValue === ANALYTICS_TAB_INDEX && (
        <Box role="tabpanel" aria-label="Analytics" sx={{ bgcolor: '#EDE5D6' }}>
          <AnalyticsPanel
            summaries={summaries}
            withheldRows={withheldRows}
            styleNames={styleNames}
          />
        </Box>
      )}

      <StyleEditDialog
        open={editorOpen}
        tab={editorTab}
        editing={editingRow}
        editingKey={editingKey}
        saving={saving}
        onClose={() => setEditorOpen(false)}
        onSubmit={submitEditor}
      />

      {confirmCopy && (
        <QuillActionConfirm
          open={pending != null}
          title={confirmCopy.title}
          message={confirmCopy.message}
          detail={confirmCopy.detail}
          caption={PRODUCTION_CAPTION}
          confirmLabel={confirmCopy.confirmLabel}
          destructive={confirmCopy.destructive}
          loading={actionBusy}
          pendingLabel="Working…"
          onConfirm={runPending}
          onCancel={() => setPending(null)}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={7000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIStylesPage;
